import { INR } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const pct = (v) => (v === null || v === undefined ? "N/A" : `${Number(v).toFixed(1)}%`);

function Card({ label, value, sub, testid, tone = "" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold mono ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function RealizationCards({ data, win }) {
  const s = data.summary[String(win)];
  const c = data.status_counts;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="realization-cards">
      <Card testid="rz-card-outstanding" label="Current Outstanding" value={INR(data.total_outstanding)} sub={`${data.rows.length} customers · as of ${data.as_of}`} tone="text-rose-800" />
      <Card testid="rz-card-exposure" label={`Total Exposure · ${win}d`} value={INR(s.exposure)} sub={`Opening ${INR(s.exposure - s.billed)} + billed ${INR(s.billed)}`} />
      <Card testid="rz-card-collected" label={`Collected · ${win}d`} value={INR(s.collected)} sub={`Recovery ${pct(s.overall_exposure_ratio)} of exposure`} tone="text-emerald-800" />
      <Card testid="rz-card-avg" label={`Avg Realization · ${win}d`} value={pct(s.avg_invoiced_ratio)} sub={`Exposure basis ${pct(s.avg_exposure_ratio)}`} />
      <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="rz-card-status">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Risk Mix</div>
        <div className="mt-1.5 flex gap-2 text-xs font-semibold">
          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 mono">{c.green} ok</span>
          <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 mono">{c.amber} watch</span>
          <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 mono">{c.red} risk</span>
        </div>
      </div>
    </div>
  );
}

export function RealizationChart({ data }) {
  const rows = data.windows.map((n) => {
    const s = data.summary[String(n)];
    return { label: `${n} days`, "Invoiced-based": s.avg_invoiced_ratio ?? 0, "Total exposure": s.avg_exposure_ratio ?? 0 };
  });
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="realization-chart-card">
      <div className="text-sm font-semibold mb-2">Average Realization Ratio <span className="text-slate-500 font-normal text-xs">· across all customers, by window</span></div>
      <div style={{ height: 220 }} data-testid="realization-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
            <CartesianGrid vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
            <YAxis domain={[0, (m) => Math.max(100, Math.ceil(m / 10) * 10)]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#475569", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={44} />
            <Tooltip formatter={(v, n) => [`${Number(v).toFixed(1)}%`, n]} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#cbd5e1" }} cursor={{ fill: "#f1f5f9" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="square" iconSize={10} />
            <Bar dataKey="Invoiced-based" fill="#1d4ed8" radius={[3, 3, 0, 0]} maxBarSize={48} />
            <Bar dataKey="Total exposure" fill="#0f766e" radius={[3, 3, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
